import type { Post } from "@/entities/post/index.js";

export type BoardIdentityPolicy = "default_public" | "force_anonymous";
export type BoardPins = Record<string, string[]>;

export type ParentBoard = {
  id: string;
  name: string;
  desc: string;
  children: string[];
  /** H5 main.js 1349-1413: every parent board carries a brand colour that
   * feeds `<button class="wall-board-chip" style="--chip-color: ...">` and
   * the chip-dot fill. */
  color: string;
};

export type BoardVote = {
  name: string;
  support: number;
  target: number;
};

export type Board = {
  id: string;
  parentId: string;
  name: string;
  desc: string;
  identityPolicy: BoardIdentityPolicy;
  moderators: string[];
  admins: string[];
  votes: BoardVote;
  homeLevel: 1 | 2 | 3 | 4;
};

export type BoardContext =
  | {
      kind: "parent";
      id: string;
      shortName: string;
      title: string;
      parentLabel: string;
      desc: string;
      itemBoardIds: string[];
      postBoardId: string;
      childNames: string[];
      parent: ParentBoard;
      childBoards: Board[];
    }
  | {
      kind: "board";
      id: string;
      shortName: string;
      title: string;
      parentLabel: string;
      desc: string;
      itemBoardIds: string[];
      postBoardId: string;
      childNames: string[];
      parent: ParentBoard;
      board: Board;
      childBoards: Board[];
    };

export const parentBoards: ParentBoard[] = [
  {
    id: "daily",
    name: "日常生活",
    desc: "食宿、宿舍、通勤和 campus 小事，承接最高频、最低门槛的日常表达。",
    children: ["life", "food", "dorm"],
    color: "#2f725d",
  },
  {
    id: "relations",
    name: "情绪关系",
    desc: "匿名、半公开和公开表达分层容纳，适合心事、人际和亲密关系。",
    children: ["emotion", "relationship", "night"],
    color: "#9a4d66",
  },
  {
    id: "learning",
    name: "学业成长",
    desc: "课程、资料、复习、升学和职业发展，给长期目标一个清晰入口。",
    children: ["study", "course", "career"],
    color: "#516f98",
  },
  {
    id: "support",
    name: "互助求助",
    desc: "求助、失物、安全提醒和紧急信息进入更明确的协作与值守链路。",
    children: ["help", "lost", "safety"],
    color: "#c65f43",
  },
  {
    id: "companions",
    name: "搭子同行",
    desc: "学习、运动、吃饭、出行和临时组队，强调可连接、可履约。",
    children: ["buddy", "sport", "travel"],
    color: "#386f9d",
  },
  {
    id: "interests",
    name: "兴趣活动",
    desc: "社团、活动、观影阅读和创作展示，让弱关系从共同兴趣开始。",
    children: ["club", "culture", "creation"],
    color: "#7a5a96",
  },
  {
    id: "exchange",
    name: "交换流转",
    desc: "闲置、拼单、技能交换和轻交易，默认公开以降低信任成本。",
    children: ["market", "groupbuy", "skill"],
    color: "#7a6b36",
  },
  {
    id: "civic",
    name: "校园公共",
    desc: "校务反馈、公共议题和板块共建，重要板块进入站务管理视野。",
    children: ["campus", "proposal", "governance"],
    color: "#5d665c",
  },
  {
    id: "wellbeing",
    name: "身心健康",
    desc: "身体状态、心理支持和作息照护，把脆弱信息放在更温和的规则里。",
    children: ["wellness", "counseling", "sleep"],
    color: "#4f7f76",
  },
];

export const boards: Board[] = [
  createBoard("life", "daily", "生活碎片", "那些很小但真实的日常，适合低门槛贴在墙上。", {
    moderators: ["阿青", "禾木"],
    admins: ["南门", "小满"],
    votes: { name: "晨间问候", support: 21, target: 28 },
  }),
  createBoard("food", "daily", "食堂与咖啡", "窗口、排队、口味和临时推荐，适合快速更新。", {
    moderators: ["禾木"],
    admins: ["小满"],
    votes: { name: "今日菜单", support: 18, target: 24 },
  }),
  createBoard("dorm", "daily", "宿舍与通勤", "宿舍设施、通勤路线、门禁与生活效率信息。", {
    moderators: ["南门"],
    admins: ["值班同学"],
    votes: { name: "维修互助", support: 16, target: 24 },
  }),
  createBoard("emotion", "relations", "情感", "默认公开，适合放下还没准备好当面说出口的话。", {
    moderators: ["晚灯"],
    admins: ["Echo", "北桥"],
    votes: { name: "失眠树洞", support: 31, target: 36 },
  }),
  createBoard(
    "relationship",
    "relations",
    "人际关系",
    "室友、朋友、团队协作和边界感，鼓励温和求证。",
    {
      moderators: ["北桥"],
      admins: ["Echo"],
      votes: { name: "边界练习室", support: 17, target: 24 },
    },
  ),
  createBoard(
    "night",
    "relations",
    "夜间树洞",
    "深夜低声区，强制匿名，适合不想被身份打扰的心事。",
    {
      identityPolicy: "force_anonymous",
      moderators: ["晚灯", "站务"],
      admins: ["Echo"],
      votes: { name: "睡前电台", support: 22, target: 30 },
      homeLevel: 4,
    },
  ),
  createBoard("study", "learning", "学习", "资料、组队、复习节奏和临近考试的互相托举。", {
    moderators: ["林间"],
    admins: ["小白"],
    votes: { name: "期末资料库", support: 27, target: 36 },
  }),
  createBoard("course", "learning", "课程答疑", "课程选择、作业疑问、经验复盘，强调可复用信息。", {
    moderators: ["林间", "站务"],
    admins: ["小白", "栗子"],
    votes: { name: "选课避坑", support: 31, target: 36 },
  }),
  createBoard("career", "learning", "升学与职业", "实习、竞赛、简历、保研考研和行业信息。", {
    moderators: ["Mori"],
    admins: ["Lynn"],
    votes: { name: "简历互看", support: 20, target: 28 },
  }),
  createBoard("help", "support", "求助", "重要求助进入管理提醒，回复优先显示可执行信息。", {
    moderators: ["站务", "南门"],
    admins: ["值班同学", "栗子"],
    votes: { name: "课程答疑", support: 18, target: 24 },
  }),
  createBoard("lost", "support", "失物招领", "捡到、寻找、认领都在这里，信息越具体越好。", {
    moderators: ["栗子"],
    admins: ["值班同学"],
    votes: { name: "认领柜台", support: 16, target: 24 },
  }),
  createBoard("safety", "support", "安全提醒", "临时风险、设施异常和需要快速扩散的提醒。", {
    moderators: ["站务"],
    admins: ["值班同学", "南门"],
    votes: { name: "夜归路线", support: 19, target: 24 },
  }),
  createBoard("buddy", "companions", "寻找搭子", "学习、吃饭和临时同行，默认公开更方便连接。", {
    moderators: ["Mori"],
    admins: ["Lynn"],
    votes: { name: "晨跑搭子", support: 13, target: 20 },
  }),
  createBoard("sport", "companions", "运动搭子", "球类、跑步、健身和轻运动，强调时间地点明确。", {
    moderators: ["北桥"],
    admins: ["Mori"],
    votes: { name: "夜跑小队", support: 17, target: 24 },
  }),
  createBoard("travel", "companions", "出行同行", "校区通勤、周末短途、返乡同行和拼车信息。", {
    moderators: ["南门"],
    admins: ["Lynn"],
    votes: { name: "周末短途", support: 12, target: 20 },
  }),
  createBoard("club", "interests", "社团活动", "招新、排练、比赛和开放活动，适合公开身份。", {
    moderators: ["小白"],
    admins: ["禾木"],
    votes: { name: "开放排练", support: 22, target: 28 },
  }),
  createBoard("culture", "interests", "观影阅读", "书影音、展览、讲座和轻讨论，适合低压力交流。", {
    moderators: ["阿青"],
    admins: ["Echo"],
    votes: { name: "周五放映", support: 24, target: 28 },
  }),
  createBoard(
    "creation",
    "interests",
    "创作展示",
    "摄影、文字、设计和作品互评，可以自行选择身份。",
    {
      moderators: ["晚灯"],
      admins: ["小满"],
      votes: { name: "作品互评", support: 20, target: 28 },
    },
  ),
  createBoard("market", "exchange", "闲置", "书、手作、小物件，也可以是一次很轻的交换。", {
    moderators: ["小满"],
    admins: ["南门"],
    votes: { name: "周末摊位", support: 19, target: 24 },
  }),
  createBoard("groupbuy", "exchange", "拼单", "小额拼单、一起下单和临时采购，默认公开降低误会。", {
    moderators: ["小满"],
    admins: ["南门"],
    votes: { name: "水果拼单", support: 18, target: 24 },
  }),
  createBoard("skill", "exchange", "技能交换", "修图、剪辑、语言、作业工具和经验互换。", {
    moderators: ["Lynn"],
    admins: ["林间"],
    votes: { name: "简历排版", support: 15, target: 24 },
  }),
  createBoard("campus", "civic", "校园事务", "公共设施、校务体验和需要被看见的问题。", {
    moderators: ["站务"],
    admins: ["值班同学"],
    votes: { name: "连廊照明", support: 28, target: 36 },
  }),
  createBoard("proposal", "civic", "反馈建议", "对板块、规则和校园公共体验提出可投票建议。", {
    moderators: ["站务", "阿青"],
    admins: ["南门"],
    votes: { name: "首页低密度模式", support: 20, target: 28 },
  }),
  createBoard("governance", "civic", "板块共建", "建版申请、版主招募、规则讨论和共建值激励。", {
    moderators: ["站务"],
    admins: ["值班同学", "小满"],
    votes: { name: "活动地图", support: 23, target: 28 },
  }),
  createBoard("wellness", "wellbeing", "身体状态", "生病、就医、运动恢复和生活作息提醒。", {
    moderators: ["栗子"],
    admins: ["值班同学"],
    votes: { name: "校医院导航", support: 18, target: 24 },
  }),
  createBoard("counseling", "wellbeing", "心理支持", "默认公开，强调支持性回应和求助资源链接。", {
    moderators: ["晚灯", "站务"],
    admins: ["Echo"],
    votes: { name: "情绪急救箱", support: 26, target: 36 },
  }),
  createBoard("sleep", "wellbeing", "睡眠作息", "作息打卡、失眠陪伴和轻量习惯互助。", {
    moderators: ["晚灯"],
    admins: ["Echo"],
    votes: { name: "早睡契约", support: 14, target: 24 },
  }),
];

export function getBoard(id: string): Board | null {
  return boards.find((board) => board.id === id) ?? null;
}

export function getParentBoard(id: string): ParentBoard | null {
  return parentBoards.find((board) => board.id === id) ?? null;
}

export function getChildBoards(parentId: string): Board[] {
  return boards.filter((board) => board.parentId === parentId);
}

/**
 * H5 main.js 1349-1413: child boards inherit their parent's brand color for
 * the `--chip-color` CSS var consumed by `.wall-board-chip` / `.quick-board-chip`
 * / `.child-board-chip` styles. Only ParentBoard carries `color`; child Board
 * does not. Centralizing this lookup avoids passing optional color around.
 *
 * Falls back to a neutral gray when the parent is missing (e.g. orphaned data),
 * matching the H5 default chip rendering.
 */
export function getBoardChipColor(board: Board): string {
  return getParentBoard(board.parentId)?.color ?? "rgba(0, 0, 0, 0.16)";
}

export function getBoardContext(id: string): BoardContext | null {
  const parent = getParentBoard(id);
  if (parent) {
    const childBoards = getChildBoards(parent.id);
    return {
      kind: "parent",
      id: parent.id,
      shortName: parent.name,
      title: `${parent.name}总墙`,
      parentLabel: "大板块",
      desc: parent.desc,
      itemBoardIds: childBoards.map((board) => board.id),
      postBoardId: childBoards[0]?.id ?? boards[0]?.id ?? parent.id,
      childNames: childBoards.map((board) => board.name),
      parent,
      childBoards,
    };
  }

  const board = getBoard(id);
  if (!board) return null;
  const parentBoard = getParentBoard(board.parentId);
  if (!parentBoard) return null;
  return {
    kind: "board",
    id: board.id,
    shortName: board.name,
    title: `${board.name}墙`,
    parentLabel: parentBoard.name,
    desc: board.desc,
    itemBoardIds: [board.id],
    postBoardId: board.id,
    childNames: [board.name],
    parent: parentBoard,
    board,
    childBoards: getChildBoards(parentBoard.id),
  };
}

export function filterPostsForBoardContext(posts: readonly Post[], context: BoardContext): Post[] {
  return posts.filter((post) => postBelongsToBoardContext(post, context));
}

export function sortPinnedPostsForBoardContext(
  posts: readonly Post[],
  context: BoardContext,
  boardPins: BoardPins,
): Post[] {
  return posts
    .map((post, index) => ({
      post,
      index,
      pinned: isPostPinnedForBoardContext(post, context, boardPins),
    }))
    .sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return a.index - b.index;
    })
    .map((entry) => entry.post);
}

export function isPostPinnedForBoardContext(
  post: Post,
  context: BoardContext,
  boardPins: BoardPins,
): boolean {
  const displayBoardId = getDisplayBoardIdForPost(post, context);
  return Boolean(displayBoardId && boardPins[displayBoardId]?.includes(post.id));
}

export function getDisplayBoardIdForPost(post: Post, context: BoardContext): string {
  if (context.kind === "board") return context.id;
  return post.boardIds.find((boardId) => context.itemBoardIds.includes(boardId)) ?? post.boardId;
}

export function postBelongsToBoardContext(post: Post, context: BoardContext): boolean {
  return postBelongsToBoardIds(post, context.itemBoardIds);
}

export function countPostsForBoardIds(posts: readonly Post[], boardIds: readonly string[]): number {
  return posts.filter((post) => postBelongsToBoardIds(post, boardIds)).length;
}

function postBelongsToBoardIds(post: Post, boardIds: readonly string[]): boolean {
  const targetIds = new Set(boardIds);
  const ids = new Set([post.boardId, ...post.boardIds].filter(Boolean));
  return [...ids].some((boardId) => targetIds.has(boardId));
}

export function normalizeBoardPins(value: unknown): BoardPins {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).map(([boardId, postIds]) => [
      boardId,
      Array.isArray(postIds)
        ? [...new Set(postIds.map((postId) => String(postId).trim()).filter(Boolean))]
        : [],
    ]),
  );
}

function createBoard(
  id: string,
  parentId: string,
  name: string,
  desc: string,
  overrides: Partial<
    Pick<Board, "identityPolicy" | "moderators" | "admins" | "votes" | "homeLevel">
  >,
): Board {
  return {
    id,
    parentId,
    name,
    desc,
    identityPolicy: overrides.identityPolicy ?? "default_public",
    moderators: overrides.moderators ?? [],
    admins: overrides.admins ?? [],
    votes: overrides.votes ?? { name: "", support: 0, target: 0 },
    homeLevel: overrides.homeLevel ?? 2,
  };
}
